import { useState, useEffect, useRef } from 'react';
import { formatDateNL } from '../utils/dateFormat';
import { axiosInstance } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Trash2, FileText, Edit, Eye, Printer, Download, X, ArrowRightLeft, Link2, Check, XCircle, Mail, Clock, Copy, Sparkles, FileUp } from 'lucide-react';
import { toast } from 'sonner';
import html2pdf from 'html2pdf.js';
import EmailSendModal from './EmailSendModal';
import { RebrandPdfDialog } from './RebrandPdfDialog';
import { StatusActionDropdown } from './StatusActionDropdown';
import { PaymentPlanEditor, PaymentPlanBlock, InstallmentInvoiceActions } from './PaymentPlan';
import { StandardTermsBlock } from './StandardTerms';
import EmailHistoryModal from './EmailHistoryModal';
import ImportDocumentModal from './ImportDocumentModal';

const COMPANY = {
  name: 'Cardoso Bouw En Onderhoud',
  address: 'Vuntuslaan 69',
  city: '1231 NR Loosdrecht',
  phone: '0685071009',
  email: 'info@cardosobouw.nl',
  kvk: '85310646',
  btw_id: 'NL004077138B58',
  iban: 'NL33INGB0709828160',
  bic: 'INGBNL2A',
};

const emptyItem = { description: '', quantity: 1, unit_price: 0, discount: 0, btw: 0 };

const Quotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [schedules, setSchedules] = useState([]); // used to show first agenda date per quote
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [previewQuote, setPreviewQuote] = useState(null);
  const [clientAddresses, setClientAddresses] = useState([]);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailHistoryQuote, setEmailHistoryQuote] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [rebrandOpen, setRebrandOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const printRef = useRef(null);

  const [form, setForm] = useState({
    client_name: '', _client_id: '', client_address: '', client_city: '',
    client_email: '', client_phone: '',
    description: '', btw_status: 'vrijgesteld',
    offer_date: new Date().toISOString().slice(0, 10),
    expiry_date: '',
    items: [{ ...emptyItem }],
    general_discount: 0, notes: '',
    down_payment_percentage: 0,
    down_payment_label: '',
    payment_plan: null,
  });

  useEffect(() => { fetchQuotes(); fetchClients(); fetchSchedules(); }, []);

  const fetchSchedules = async () => {
    try { const r = await axiosInstance.get('/schedule'); setSchedules(r.data || []); } catch { /* noop */ }
  };

  // Match a quote to its earliest planned schedule by fuzzy client_name + address match.
  // Returns { date: 'YYYY-MM-DD', time: 'HH:MM' } or null.
  const findFirstScheduleFor = (q) => {
    if (!q || !schedules?.length) return null;
    const qName = (q.client_name || '').trim().toLowerCase();
    const qAddr = (q.client_address || '').trim().toLowerCase();
    if (!qName && !qAddr) return null;
    const matches = schedules.filter((s) => {
      const sName = (s.client_name || '').trim().toLowerCase();
      const sAddr = (s.client_address || '').trim().toLowerCase();
      const nameMatch = qName && sName && (sName === qName || sName.includes(qName) || qName.includes(sName));
      const addrMatch = qAddr && sAddr && (sAddr === qAddr || sAddr.includes(qAddr) || qAddr.includes(sAddr));
      // Require BOTH name AND address to match (or name if no address), to avoid false positives.
      if (qAddr) return nameMatch && addrMatch;
      return nameMatch;
    });
    if (!matches.length) return null;
    matches.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    return { date: matches[0].date, time: matches[0].time };
  };

  const fetchQuotes = async () => {
    try { setLoading(true); const res = await axiosInstance.get('/quotes'); setQuotes(res.data); }
    catch { toast.error('Fout bij laden offertes'); }
    finally { setLoading(false); }
  };

  const fetchClients = async () => {
    try { const res = await axiosInstance.get('/clients/all'); setClients(res.data); } catch {}
  };

  const handleClientChange = (name) => {
    const client = clients.find(c => c.name === name);
    const addrs = client?.addresses?.map(a => {
      const full = (a.address || a.street || '').trim();
      // Split on last comma → street | city
      const idx = full.lastIndexOf(',');
      const street = idx > -1 ? full.slice(0, idx).trim() : full;
      const city = idx > -1 ? full.slice(idx + 1).trim() : (a.city || '');
      return { id: a.id, label: a.label || '', email: a.email || '', phone: a.phone || '', full, street, city };
    }) || [];
    setClientAddresses(addrs);
    setForm(f => ({
      ...f,
      _client_id: name,
      client_name: name,
      client_address: '',
      client_city: '',
      client_email: '',
      client_phone: '',
    }));
  };

  const handleAddressChange = (addrId) => {
    const addr = clientAddresses.find(a => a.id === addrId);
    if (!addr) return;
    setForm(f => ({
      ...f,
      client_name: addr.label ? addr.label : f.client_name,
      client_address: addr.street,
      client_city: addr.city || f.client_city,
      client_email: addr.email || f.client_email,
      client_phone: addr.phone || f.client_phone,
      _selected_addr_id: addrId,
    }));
  };

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { ...emptyItem }] }));
  const removeItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const moveItem = (from, to) => setForm(f => {
    if (to < 0 || to >= f.items.length || from === to) return f;
    const items = [...f.items]; const [m] = items.splice(from, 1); items.splice(to, 0, m);
    return { ...f, items };
  });
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const updateItem = (i, field, value) => {
    setForm(f => {
      const items = [...f.items];
      items[i] = { ...items[i], [field]: value };
      return { ...f, items };
    });
  };

  const calcLineTotal = (item) => {
    let t = item.quantity * item.unit_price;
    if (item.discount > 0) t *= (1 - item.discount / 100);
    return t;
  };

  const calcSubtotal = () => form.items.reduce((s, i) => s + calcLineTotal(i), 0);
  const calcBtwTotals = () => {
    const totals = {};
    form.items.forEach(i => {
      const rate = parseFloat(i.btw) || 0;
      if (rate > 0) {
        const amt = calcLineTotal(i) * rate / 100;
        const key = `${rate}%`;
        totals[key] = (totals[key] || 0) + amt;
      }
    });
    return totals;
  };
  const calcTotalBtw = () => Object.values(calcBtwTotals()).reduce((a, b) => a + b, 0);

  const handleSubmit = async () => {
    if (!form.client_name) { toast.error('Selecteer een klant'); return; }
    if (!form.items.length) { toast.error('Voeg minimaal een regel toe'); return; }

    const payload = {
      ...form,
      offer_date: form.offer_date ? formatDateNL(form.offer_date) : '',
      expiry_date: form.expiry_date ? formatDateNL(form.expiry_date) : '',
      general_discount: parseFloat(form.general_discount) || 0,
      down_payment_percentage: parseFloat(form.down_payment_percentage) || 0,
      down_payment_label: (form.down_payment_label || '').trim() || null,
      payment_plan: form.payment_plan || null,
      items: form.items.map(i => ({
        ...i, quantity: parseFloat(i.quantity) || 0, unit_price: parseFloat(i.unit_price) || 0,
        discount: parseFloat(i.discount) || 0, btw: parseFloat(i.btw) || 0,
      })),
    };

    try {
      if (editingId) {
        await axiosInstance.put(`/quotes/${editingId}`, payload);
        toast.success('Offerte bijgewerkt!');
      } else {
        await axiosInstance.post('/quotes', payload);
        toast.success('Offerte aangemaakt!');
      }
      resetForm();
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fout bij opslaan offerte');
    }
  };

  const resetForm = () => {
    setShowForm(false); setEditingId(null); setClientAddresses([]);
    setForm({ client_name: '', _client_id: '', client_address: '', client_city: '', client_email: '', client_phone: '', description: '', btw_status: 'vrijgesteld',
      offer_date: new Date().toISOString().slice(0, 10), expiry_date: '',
      items: [{ ...emptyItem }], general_discount: 0, notes: '', down_payment_percentage: 0, down_payment_label: '', payment_plan: null });
  };

  const handleEdit = (q) => {
    setEditingId(q.id);
    const client = clients.find(c => c.name === q.client_name);
    setClientAddresses(client?.addresses?.map(a => {
      const full = (a.address || a.street || '').trim();
      const idx = full.lastIndexOf(',');
      const street = idx > -1 ? full.slice(0, idx).trim() : full;
      const city = idx > -1 ? full.slice(idx + 1).trim() : (a.city || '');
      return { id: a.id, label: a.label || '', email: a.email || '', phone: a.phone || '', full, street, city };
    }) || []);
    const toDateInput = (d) => {
      if (!d) return '';
      const parts = d.split('-');
      if (parts[0].length === 4) return d;
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    };
    setForm({
      client_name: q.client_name, _client_id: '', client_address: q.client_address, client_city: q.client_city,
      client_email: q.client_email || '', client_phone: q.client_phone || '',
      description: q.description, btw_status: q.btw_status || 'vrijgesteld',
      offer_date: toDateInput(q.offer_date), expiry_date: toDateInput(q.expiry_date),
      items: q.items?.length ? q.items.map(i => ({ description: i.description, quantity: i.quantity, unit_price: i.unit_price, discount: i.discount || 0, btw: i.btw || 0 })) : [{ ...emptyItem }],
      general_discount: q.general_discount || 0, notes: q.notes || '',
      down_payment_percentage: q.down_payment_percentage || 0,
      down_payment_label: q.down_payment_label || '',
      payment_plan: q.payment_plan || null,
    });
    setShowForm(true);
  };

  const handleConvertToInvoice = async (quoteId, quoteName) => {
    if (!window.confirm(`Offerte van "${quoteName}" omzetten naar factuur?`)) return;
    try {
      const res = await axiosInstance.post(`/quotes/${quoteId}/to-invoice`);
      toast.success(`Factuur ${res.data.number} aangemaakt vanuit offerte!`);
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fout bij omzetten');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deze offerte verwijderen?')) return;
    try { await axiosInstance.delete(`/quotes/${id}`); toast.success('Offerte verwijderd'); fetchQuotes(); }
    catch { toast.error('Fout bij verwijderen offerte'); }
  };

  const _suggestNextLabel = (current) => {
    const c = (current || 'Aanbetaling').trim().toLowerCase();
    if (c === 'aanbetaling') return 'Tussenbetaling';
    if (c.startsWith('tussenbetaling')) return 'Eindbetaling';
    if (c === 'eindbetaling') return 'Eindbetaling';
    return 'Tussenbetaling';
  };

  const handleDuplicateNext = async (q) => {
    const suggested = _suggestNextLabel(q.down_payment_label);
    const newLabel = window.prompt(
      `Volgende offerte aanmaken voor "${q.client_name}".\n\nNieuwe label voor de betaling:\n(Aanbetaling → Tussenbetaling → Eindbetaling)`,
      suggested
    );
    if (newLabel === null) return;
    const labelClean = newLabel.trim() || suggested;
    const pctStr = window.prompt(`Nieuw percentage voor "${labelClean}" (0-100):`, String(q.down_payment_percentage || 0));
    if (pctStr === null) return;
    const pct = Math.max(0, Math.min(100, parseFloat(pctStr) || 0));
    try {
      const res = await axiosInstance.post(`/quotes/${q.id}/duplicate-next`, {
        down_payment_label: labelClean,
        down_payment_percentage: pct,
      });
      toast.success(`Volgende offerte aangemaakt: ${res.data.number} (${labelClean})`);
      fetchQuotes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fout bij dupliceren');
    }
  };

  const handleDuplicate = async (q) => {
    if (!window.confirm(`Een exacte kopie van offerte ${q.number} maken voor "${q.client_name}"?\n\nNieuw nummer wordt automatisch toegekend, datum = vandaag, status = concept.`)) return;
    try {
      const res = await axiosInstance.post(`/quotes/${q.id}/duplicate`);
      toast.success(`Offerte gedupliceerd: ${res.data.number}`);
      fetchQuotes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fout bij dupliceren');
    }
  };

  const handlePrint = () => {
    const w = window.open('', '_blank');
    w.document.write(printRef.current.innerHTML);
    w.document.write('<style>@media print { body { margin: 0; } } body { font-family: Arial, sans-serif; }</style>');
    w.document.close();
    w.print();
  };

  const handleDownloadPdf = () => {
    if (!printRef.current || !previewQuote) return;
    const filename = `Offerte_${previewQuote.number}_${(previewQuote.client_name || '').replace(/[^a-z0-9]/gi, '_')}.pdf`;
    const opts = {
      margin: 0,
      filename,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['css', 'legacy'], before: '.html2pdf__page-break' },
    };
    html2pdf().set(opts).from(printRef.current).save().then(() => toast.success('PDF gedownload')).catch(() => toast.error('Fout bij downloaden PDF'));
  };

  // Send Email: open preview for this quote first so the PDF DOM is rendered,
  // then on next tick open the email modal which will use printRef.current.
  const handleOpenEmail = (q) => {
    setPreviewQuote(q);
    setTimeout(() => setEmailModalOpen(true), 100);
  };

  const fmtEuro = (v) => `€ ${(Number(v) || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // Fallback: compute BTW totals from items if not present in DB (legacy quotes)
  const getQuoteTotals = (q) => {
    if (!q) return { subtotal: 0, btw_totals: {}, total_btw: 0, total: 0, is_verlegd: false };
    const is_verlegd = (q.btw_status || '').toLowerCase() === 'verlegd';
    if (q.btw_totals && Object.keys(q.btw_totals).length > 0 && !is_verlegd) {
      return { subtotal: q.subtotal || 0, btw_totals: q.btw_totals, total_btw: q.total_btw || 0, total: q.total || 0, is_verlegd };
    }
    let subtotal = 0;
    const btw_totals = {};
    (q.items || []).forEach(it => {
      const lt = it.line_total != null ? it.line_total : (it.quantity || 0) * (it.unit_price || 0) * (1 - (it.discount || 0) / 100);
      subtotal += lt;
      if (is_verlegd) return;
      const rate = parseFloat(it.btw) || 0;
      if (rate > 0) {
        const amt = lt * rate / 100;
        const key = `${rate}%`;
        btw_totals[key] = (btw_totals[key] || 0) + amt;
      }
    });
    const total_btw = is_verlegd ? 0 : Object.values(btw_totals).reduce((a, b) => a + b, 0);
    const total = subtotal - (q.general_discount || 0) + total_btw;
    return { subtotal, btw_totals, total_btw, total, is_verlegd };
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-4xl font-bold text-gray-800">Offertes</h1>
        <div className="flex gap-2">
          <Button onClick={() => setRebrandOpen(true)} variant="outline" className="border-gray-300 text-gray-700" data-testid="rebrand-pdf-btn">
            <FileUp className="w-4 h-4 mr-2" /> PDF op eigen naam
          </Button>
          <Button onClick={() => setImportOpen(true)} variant="outline" className="border-[#3da5a5] text-[#3da5a5] hover:bg-[#3da5a5]/10" data-testid="import-quote-btn">
            <Sparkles className="w-4 h-4 mr-2" /> Importeer (IA)
          </Button>
          <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-[#3da5a5] hover:bg-[#2d8585]" data-testid="new-quote-btn">
            <Plus className="w-4 h-4 mr-2" /> Nieuwe Offerte
          </Button>
        </div>
      </div>

      {/* FORM */}
      {showForm && (
        <Card className="bg-white shadow-sm border-gray-200" data-testid="quote-form">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-gray-800">{editingId ? 'Offerte bewerken' : 'Nieuwe Offerte'}</CardTitle>
            <Button variant="ghost" size="sm" onClick={resetForm}><X className="w-4 h-4" /></Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-700 text-xs text-gray-500">Klant laden (optioneel)</Label>
                <Select value={form._client_id || ''} onValueChange={handleClientChange}>
                  <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="quote-client-select"><SelectValue placeholder="Kies een opgeslagen klant..." /></SelectTrigger>
                  <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {clientAddresses.length > 0 && (
                <div>
                  <Label className="text-gray-700 text-xs text-gray-500">Adres laden (optioneel)</Label>
                  <Select value={form._selected_addr_id || ''} onValueChange={handleAddressChange}>
                    <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="quote-address-select"><SelectValue placeholder="Kies een opgeslagen adres..." /></SelectTrigger>
                    <SelectContent>{clientAddresses.map(a => <SelectItem key={a.id} value={a.id}>{a.label ? `${a.label} — ${a.full}` : a.full}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-gray-700">Naam Klant *</Label>
                <Input value={form.client_name} onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))} placeholder="Naam invoeren" className="bg-white border-gray-200" data-testid="quote-client-name-input" />
              </div>
              <div>
                <Label className="text-gray-700">Adres</Label>
                <Input value={form.client_address} onChange={e => setForm(f => ({ ...f, client_address: e.target.value }))} placeholder="Adres invoeren" className="bg-white border-gray-200" data-testid="quote-client-address-input" />
              </div>
              <div>
                <Label className="text-gray-700">Plaats</Label>
                <Input value={form.client_city} onChange={e => setForm(f => ({ ...f, client_city: e.target.value }))} placeholder="Plaats invoeren" className="bg-white border-gray-200" data-testid="quote-client-city-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-700">Email</Label>
                <Input type="email" value={form.client_email} onChange={e => setForm(f => ({ ...f, client_email: e.target.value }))} placeholder="klant@email.com" className="bg-white border-gray-200" data-testid="quote-client-email-input" />
              </div>
              <div>
                <Label className="text-gray-700">Telefoon</Label>
                <Input value={form.client_phone} onChange={e => setForm(f => ({ ...f, client_phone: e.target.value }))} placeholder="+31 ..." className="bg-white border-gray-200" data-testid="quote-client-phone-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div><Label className="text-gray-700">Omschrijving</Label><Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="bg-gray-50 border-gray-200" /></div>
              <div><Label className="text-gray-700">Offertedatum</Label><Input type="date" value={form.offer_date} onChange={e => setForm(f => ({ ...f, offer_date: e.target.value }))} className="bg-gray-50 border-gray-200" /></div>
              <div><Label className="text-gray-700">Vervaldatum</Label><Input type="date" value={form.expiry_date} onChange={e => setForm(f => ({ ...f, expiry_date: e.target.value }))} className="bg-gray-50 border-gray-200" /></div>
            </div>

            {/* BTW status selector — normal vs verlegd (B2B reverse charge) */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <Label className="text-gray-700 font-semibold flex items-center gap-2">
                <span>📋 BTW-regeling</span>
              </Label>
              <div className="flex flex-wrap gap-3 mt-2">
                <label className={`flex items-center gap-2 px-3 py-2 rounded border cursor-pointer transition-colors ${(form.btw_status === 'normal' || form.btw_status === 'vrijgesteld' || !form.btw_status) ? 'bg-white border-[#3da5a5] ring-2 ring-[#3da5a5]/20' : 'bg-gray-50 border-gray-200'}`}>
                  <input
                    type="radio"
                    name="btw_status_quote"
                    value="normal"
                    checked={form.btw_status !== 'verlegd'}
                    onChange={() => setForm(f => ({ ...f, btw_status: 'normal' }))}
                    data-testid="quote-btw-status-normal"
                  />
                  <span className="text-sm">Normaal — BTW per regel (0% / 9% / 21%)</span>
                </label>
                <label className={`flex items-center gap-2 px-3 py-2 rounded border cursor-pointer transition-colors ${form.btw_status === 'verlegd' ? 'bg-white border-[#3da5a5] ring-2 ring-[#3da5a5]/20' : 'bg-gray-50 border-gray-200'}`}>
                  <input
                    type="radio"
                    name="btw_status_quote"
                    value="verlegd"
                    checked={form.btw_status === 'verlegd'}
                    onChange={() => setForm(f => ({ ...f, btw_status: 'verlegd' }))}
                    data-testid="quote-btw-status-verlegd"
                  />
                  <span className="text-sm font-medium text-amber-900">BTW verlegd — afnemer is btw-plichtig</span>
                </label>
              </div>
              {form.btw_status === 'verlegd' && (
                <p className="text-xs text-amber-700 mt-2 italic">⚠️ Bij BTW verlegd wordt geen btw berekend; de afnemer is verantwoordelijk voor de btw-afdracht (omgekeerde heffing). Vermelding op offerte: "BTW verlegd".</p>
              )}
            </div>

            {/* Line Items */}
            <div>
              <Label className="text-gray-700 font-semibold">Regels</Label>
              <p className="text-xs text-gray-400">Sleep een regel aan het handvat (⋮⋮) of gebruik de pijltjes om de volgorde te wijzigen.</p>
              <div className="space-y-2 mt-2">
                {form.items.map((item, i) => (
                  <div key={i} data-testid={`quote-item-row-${i}`}
                    draggable
                    onDragStart={(e) => { setDragIdx(i); e.dataTransfer.effectAllowed = 'move'; }}
                    onDragOver={(e) => { e.preventDefault(); if (overIdx !== i) setOverIdx(i); }}
                    onDragLeave={() => setOverIdx(null)}
                    onDrop={(e) => { e.preventDefault(); if (dragIdx !== null) moveItem(dragIdx, i); setDragIdx(null); setOverIdx(null); }}
                    onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
                    className={`grid grid-cols-12 gap-2 items-end p-3 rounded-lg border transition-colors ${dragIdx === i ? 'opacity-50 bg-gray-100 border-dashed border-gray-400' : overIdx === i ? 'bg-[#3da5a5]/10 border-[#3da5a5]' : 'bg-gray-50 border-transparent'}`}>
                    <div className="col-span-1 flex items-center gap-0.5 pb-1">
                      <span className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-700 select-none text-lg leading-none px-1" title="Sleep om te verplaatsen" data-testid={`quote-item-drag-${i}`}>⋮⋮</span>
                      <div className="flex flex-col">
                        <button type="button" onClick={() => moveItem(i, i - 1)} disabled={i === 0} className="text-gray-400 hover:text-[#3da5a5] disabled:opacity-20 leading-none" title="Omhoog" data-testid={`quote-item-up-${i}`}>▲</button>
                        <button type="button" onClick={() => moveItem(i, i + 1)} disabled={i === form.items.length - 1} className="text-gray-400 hover:text-[#3da5a5] disabled:opacity-20 leading-none" title="Omlaag" data-testid={`quote-item-down-${i}`}>▼</button>
                      </div>
                    </div>
                    <div className="col-span-3"><Label className="text-xs text-gray-500">Omschrijving</Label><Input value={item.description} onChange={e => updateItem(i, 'description', e.target.value)} className="bg-white border-gray-200 text-sm" data-testid={`quote-item-desc-${i}`} /></div>
                    <div className="col-span-1"><Label className="text-xs text-gray-500">Aantal</Label><Input type="number" step="0.01" value={item.quantity} onChange={e => updateItem(i, 'quantity', e.target.value)} className="bg-white border-gray-200 text-sm" /></div>
                    <div className="col-span-2"><Label className="text-xs text-gray-500">Bedrag</Label><Input type="number" step="0.01" value={item.unit_price} onChange={e => updateItem(i, 'unit_price', e.target.value)} className="bg-white border-gray-200 text-sm" /></div>
                    <div className="col-span-1"><Label className="text-xs text-gray-500">Korting %</Label><Input type="number" step="0.01" value={item.discount} onChange={e => updateItem(i, 'discount', e.target.value)} className="bg-white border-gray-200 text-sm" /></div>
                    <div className="col-span-1"><Label className="text-xs text-gray-500">BTW %</Label><Input type="number" value={item.btw} onChange={e => updateItem(i, 'btw', e.target.value)} className="bg-white border-gray-200 text-sm" /></div>
                    <div className="col-span-2 text-right font-semibold text-gray-800 text-sm pt-5">{fmtEuro(calcLineTotal(item))}</div>
                    <div className="col-span-1 flex justify-end"><Button variant="ghost" size="sm" onClick={() => removeItem(i)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button></div>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={addItem} className="mt-2 border-gray-300 text-gray-700"><Plus className="w-4 h-4 mr-1" /> Regel toevoegen</Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div><Label className="text-gray-700">Korting (€)</Label><Input type="number" step="0.01" value={form.general_discount} onChange={e => setForm(f => ({ ...f, general_discount: e.target.value }))} className="bg-gray-50 border-gray-200" /></div>
              <div>
                <Label className="text-gray-700">Aanbetaling % <span className="text-xs text-gray-400">(optioneel)</span></Label>
                <Input type="number" min="0" max="100" step="1" value={form.down_payment_percentage} onChange={e => setForm(f => ({ ...f, down_payment_percentage: e.target.value }))} placeholder="bijv. 30" className="bg-gray-50 border-gray-200" data-testid="quote-down-payment-input" />
                {parseFloat(form.down_payment_percentage) > 0 && (
                  <p className="text-xs text-gray-500 mt-1">≈ {fmtEuro(((calcSubtotal() + calcTotalBtw() - (parseFloat(form.general_discount) || 0)) * parseFloat(form.down_payment_percentage)) / 100)}</p>
                )}
              </div>
              <div>
                <Label className="text-gray-700">Naam betaling <span className="text-xs text-gray-400">(optioneel)</span></Label>
                <Input
                  type="text"
                  value={form.down_payment_label}
                  onChange={e => setForm(f => ({ ...f, down_payment_label: e.target.value }))}
                  placeholder="Aanbetaling"
                  className="bg-gray-50 border-gray-200"
                  data-testid="quote-down-payment-label-input"
                  disabled={!(parseFloat(form.down_payment_percentage) > 0)}
                />
                <p className="text-[11px] text-gray-400 mt-1">bijv. Aanbetaling, Tussenbetaling, Eindbetaling</p>
              </div>
              <div className="md:col-span-1"><Label className="text-gray-700">Opmerkingen</Label><textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="w-full bg-gray-50 border border-gray-200 rounded-lg p-2 text-sm text-gray-800 h-40" /></div>

              <div className="md:col-span-4">
                <PaymentPlanEditor value={form.payment_plan} onChange={(pp) => setForm(f => ({ ...f, payment_plan: pp }))} totalIncl={calcSubtotal() + calcTotalBtw() - (parseFloat(form.general_discount) || 0)} />
              </div>
            </div>

            <div className="flex items-center justify-between border-t border-gray-200 pt-4 flex-wrap gap-3">
              <div className="text-sm text-gray-700 space-y-1">
                <div>Subtotaal (excl. BTW): <strong>{fmtEuro(calcSubtotal())}</strong></div>
                {Object.entries(calcBtwTotals()).map(([rate, amt]) => (
                  <div key={rate}>BTW {rate}: <strong>{fmtEuro(amt)}</strong></div>
                ))}
                {parseFloat(form.general_discount) > 0 && <div className="text-red-600">Korting: -{fmtEuro(parseFloat(form.general_discount) || 0)}</div>}
                <div className="text-lg font-bold text-[#3da5a5]">Totaal incl. BTW: {fmtEuro(calcSubtotal() + calcTotalBtw() - (parseFloat(form.general_discount) || 0))}</div>
              </div>
              <Button onClick={handleSubmit} className="bg-[#3da5a5] hover:bg-[#2d8585]" data-testid="save-quote-btn">{editingId ? 'Bijwerken' : 'Offerte maken'}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* PREVIEW */}
      {previewQuote && (
        <Card className="bg-white shadow-sm border-gray-200" data-testid="quote-preview">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-gray-800">Offerte {previewQuote.number}</CardTitle>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => setEmailModalOpen(true)} className="bg-[#1a5276] hover:bg-[#143d5a]" data-testid="email-quote-from-preview"><Mail className="w-4 h-4 mr-1" /> Versturen</Button>
              <Button size="sm" onClick={handleDownloadPdf} className="bg-[#3da5a5] hover:bg-[#2d8585]" data-testid="download-quote-pdf"><Download className="w-4 h-4 mr-1" /> PDF downloaden</Button>
              <Button size="sm" onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700"><Printer className="w-4 h-4 mr-1" /> Afdrukken</Button>
              <Button variant="ghost" size="sm" onClick={() => setPreviewQuote(null)}><X className="w-4 h-4" /></Button>
            </div>
          </CardHeader>
          <CardContent>
            <div ref={printRef}>
              <div style={{ fontFamily: 'Arial, sans-serif', padding: '40px', maxWidth: '800px', margin: '0 auto', color: '#333' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px' }}>
                  <div>
                    <h2 style={{ color: '#1a5276', fontSize: '22px', margin: '0 0 20px 0' }}>Offerte {previewQuote.number}</h2>
                    <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Naam Klant:</strong> {previewQuote.client_name}</p>
                    <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Adres Klant:</strong> {previewQuote.client_address}</p>
                    <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Plaats Klant:</strong> {previewQuote.client_city}</p>
                    {previewQuote.client_email && <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>E-mail:</strong> {previewQuote.client_email}</p>}
                    {previewQuote.client_phone && <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Telefoon:</strong> {previewQuote.client_phone}</p>}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <img src="/logo-cardoso.jpeg" alt="Logo" style={{ width: '120px', marginBottom: '10px' }} />
                    <p style={{ margin: '2px 0', fontSize: '12px' }}>{COMPANY.name}</p>
                    <p style={{ margin: '2px 0', fontSize: '12px' }}>{COMPANY.address}</p>
                    <p style={{ margin: '2px 0', fontSize: '12px' }}>{COMPANY.city}</p>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', padding: '10px', background: '#f8f9fa', borderRadius: '4px' }}>
                  <div><p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Omschrijving offerte:</strong> {previewQuote.description}</p></div>
                  <div style={{ textAlign: 'right' }}><p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Offertedatum:</strong> {previewQuote.offer_date}</p><p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Vervaldatum:</strong> {previewQuote.expiry_date}</p></div>
                </div>
                {Array.isArray(previewQuote.previous_quotes_chain) && previewQuote.previous_quotes_chain.length > 0 && (
                  <div style={{ marginBottom: '18px', padding: '12px 14px', background: '#ecfdf5', border: '1px solid #10b981', borderRadius: '4px', fontSize: '12px' }}>
                    <p style={{ margin: '0 0 6px 0', fontWeight: 'bold', color: '#065f46', fontSize: '13px' }}>✓ Reeds voldane termijnen</p>
                    {previewQuote.previous_quotes_chain.map((p, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', color: '#065f46' }}>
                        <span>{p.label || 'Aanbetaling'} — offerte {p.quote_number}{p.percentage ? ` (${p.percentage}%)` : ''}{p.paid_at ? ` · betaald ${new Date(p.paid_at).toLocaleDateString('nl-NL')}` : ''}</span>
                        {p.amount && <strong>€ {Number(p.amount).toLocaleString('nl-NL', { minimumFractionDigits: 2 })}</strong>}
                      </div>
                    ))}
                  </div>
                )}
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginBottom: '20px' }}>
                  <thead><tr style={{ background: '#1a5276', color: 'white' }}>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Omschrijving</th>
                    <th style={{ padding: '8px', textAlign: 'right' }}>Aantal</th>
                    <th style={{ padding: '8px', textAlign: 'right' }}>Bedrag</th>
                    <th style={{ padding: '8px', textAlign: 'right' }}>Korting</th>
                    <th style={{ padding: '8px', textAlign: 'right' }}>BTW</th>
                    <th style={{ padding: '8px', textAlign: 'right' }}>Totaal excl. BTW</th>
                  </tr></thead>
                  <tbody>
                    {previewQuote.items?.map((item, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '8px' }}>{item.description}</td>
                        <td style={{ padding: '8px', textAlign: 'right' }}>{Number(item.quantity).toFixed(2).replace('.', ',')}</td>
                        <td style={{ padding: '8px', textAlign: 'right' }}>{fmtEuro(item.unit_price)}</td>
                        <td style={{ padding: '8px', textAlign: 'right' }}>{item.discount > 0 ? `${Number(item.discount).toFixed(2).replace('.', ',')}%` : ''}</td>
                        <td style={{ padding: '8px', textAlign: 'right' }}>{item.btw}%</td>
                        <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>{fmtEuro(item.line_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
                  <div style={{ width: '280px' }}>
                    {(() => { const t = getQuoteTotals(previewQuote); return (<>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '13px' }}><span>Subtotaal (excl. BTW)</span><strong>{fmtEuro(t.subtotal)}</strong></div>
                    {!t.is_verlegd && Object.entries(t.btw_totals || {}).map(([rate, amt]) => (
                      <div key={rate} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '13px' }}><span>BTW {rate}</span><span>{fmtEuro(amt)}</span></div>
                    ))}
                    {t.is_verlegd && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '12px', color: '#92400e', fontStyle: 'italic' }}><span>BTW verlegd</span><span>€ 0,00</span></div>
                    )}
                    {previewQuote.general_discount > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '13px', color: '#e74c3c' }}><span>Korting</span><span>-{fmtEuro(previewQuote.general_discount)}</span></div>}
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '2px solid #1a5276', fontSize: '16px', fontWeight: 'bold', color: '#1a5276' }}><span>Totaal{t.is_verlegd ? '' : ' incl. BTW'}</span><span>{fmtEuro(t.total)}</span></div>
                    </>); })()}
                  </div>
                </div>
                {(previewQuote.btw_status || '').toLowerCase() === 'verlegd' && (
                  <div style={{ padding: '10px 14px', background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: '4px', fontSize: '12px', marginBottom: '15px', color: '#78350f' }}>
                    <strong>BTW verlegd.</strong> Overeenkomstig artikel 12, vijfde lid van de Wet op de omzetbelasting 1968 is de heffing van btw verlegd naar de afnemer.
                  </div>
                )}
                {previewQuote.notes && <div style={{ padding: '15px', background: '#f8f9fa', borderRadius: '4px', fontSize: '12px', marginBottom: '20px', whiteSpace: 'pre-wrap' }}>{previewQuote.notes}</div>}

                <PaymentPlanBlock plan={previewQuote.payment_plan} totalIncl={previewQuote.total} documentNumber={previewQuote.number} documentType="Offerte" />
                <StandardTermsBlock />
                
                {/* Show signature/akkoord block if signed */}
                {previewQuote.status === 'goedgekeurd' && previewQuote.client_response && (
                  <div style={{ marginTop: '20px', padding: '15px', background: '#ecfdf5', border: '1px solid #10b981', borderRadius: '4px', fontSize: '12px' }}>
                    <p style={{ color: '#065f46', fontWeight: 'bold', marginBottom: '8px' }}>✓ Akkoord — Goedgekeurd door klant</p>
                    <p><strong>Naam:</strong> {previewQuote.client_response.signer_name}</p>
                    <p><strong>Adres:</strong> {previewQuote.client_response.signer_address}</p>
                    <p><strong>Telefoon:</strong> {previewQuote.client_response.signer_phone}</p>
                    <p><strong>Datum/tijd:</strong> {previewQuote.client_response.responded_at && new Date(previewQuote.client_response.responded_at).toLocaleString('nl-NL')}</p>
                    {previewQuote.client_response.signature_data && (
                      <div style={{ marginTop: '8px' }}>
                        <p style={{ color: '#666', fontSize: '11px', marginBottom: '4px' }}>Handtekening:</p>
                        <img src={previewQuote.client_response.signature_data} alt="handtekening" style={{ maxHeight: '80px', background: 'white', border: '1px solid #ddd', borderRadius: '4px' }} />
                      </div>
                    )}
                  </div>
                )}
                {previewQuote.status === 'afgewezen' && previewQuote.client_response && (
                  <div style={{ marginTop: '20px', padding: '15px', background: '#fef2f2', border: '1px solid #ef4444', borderRadius: '4px', fontSize: '12px' }}>
                    <p style={{ color: '#991b1b', fontWeight: 'bold', marginBottom: '8px' }}>✗ Offerte afgewezen</p>
                    {previewQuote.client_response.rejection_reason && (
                      <p><strong>Reden:</strong> {previewQuote.client_response.rejection_reason}</p>
                    )}
                    <p><strong>Datum/tijd:</strong> {previewQuote.client_response.responded_at && new Date(previewQuote.client_response.responded_at).toLocaleString('nl-NL')}</p>
                  </div>
                )}
                
                <div style={{ borderTop: '1px solid #ddd', paddingTop: '15px', fontSize: '11px', color: '#666', display: 'flex', justifyContent: 'space-between' }}>
                  <div><p>{COMPANY.name} · {COMPANY.address}, {COMPANY.city}</p><p>Mobiel: {COMPANY.phone} · E-mail: {COMPANY.email}</p></div>
                  <div style={{ textAlign: 'right' }}><p>KVK: {COMPANY.kvk} · BTW-id: {COMPANY.btw_id}</p><p>IBAN: {COMPANY.iban} · BIC: {COMPANY.bic}</p></div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* LIST */}
      {loading ? (
        <p className="text-gray-500 text-center py-8">Laden...</p>
      ) : quotes.length === 0 ? (
        <Card className="bg-white shadow-sm border-gray-200"><CardContent className="py-12 text-center"><FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" /><p className="text-gray-500">Geen offertes aangemaakt</p></CardContent></Card>
      ) : (
        <div className="space-y-3">
          {/* Search & Status Filter */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Input
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Zoek op nummer, klantnaam, adres of e-mail..."
                className="pl-3 pr-9 bg-white border-gray-200"
                data-testid="quote-search-input"
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700" data-testid="quote-search-clear">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[200px] bg-white border-gray-200" data-testid="quote-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle statussen</SelectItem>
                <SelectItem value="draft">Concept</SelectItem>
                <SelectItem value="sent">Verzonden</SelectItem>
                <SelectItem value="goedgekeurd">Akkoord</SelectItem>
                <SelectItem value="afgewezen">Afgewezen</SelectItem>
                <SelectItem value="converted">Omgezet naar factuur</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {(() => {
            const term = searchTerm.trim().toLowerCase();
            const filtered = quotes.filter((q) => {
              if (statusFilter !== 'all' && (q.status || 'draft') !== statusFilter) return false;
              if (!term) return true;
              return (
                (q.number || '').toLowerCase().includes(term) ||
                (q.client_name || '').toLowerCase().includes(term) ||
                (q.client_address || '').toLowerCase().includes(term) ||
                (q.client_email || '').toLowerCase().includes(term) ||
                (q.description || '').toLowerCase().includes(term)
              );
            });
            if (!filtered.length) {
              return <Card className="bg-white shadow-sm border-gray-200"><CardContent className="py-10 text-center"><p className="text-gray-500">Geen offertes gevonden voor je zoekopdracht.</p></CardContent></Card>;
            }
            return filtered.map(q => {
            const cr = q.client_response || {};
            const dpLabel = q.down_payment_label || 'Aanbetaling';
            const firstSchedule = findFirstScheduleFor(q);
            return (
            <Card key={q.id} className={`bg-white shadow-sm border-l-4 transition-all ${q.status === 'goedgekeurd' ? 'border-l-green-500' : q.status === 'afgewezen' ? 'border-l-red-500' : 'border-l-gray-200'} border-gray-200 hover:border-gray-300`} data-testid={`quote-${q.number}`}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-6 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-bold text-[#1a5276]">{q.number}</p>
                        {q.status === 'goedgekeurd' && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-semibold flex items-center gap-1" title={`Getekend door ${cr.signer_name || ''} op ${cr.responded_at ? new Date(cr.responded_at).toLocaleString('nl-NL') : ''}`}>
                            <Check className="w-3 h-3" /> Akkoord
                          </span>
                        )}
                        {q.status === 'afgewezen' && (
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded font-semibold flex items-center gap-1" title={cr.rejection_reason || 'Geen reden opgegeven'}>
                            <XCircle className="w-3 h-3" /> Afgewezen
                          </span>
                        )}
                        {q.mollie_payment?.status === 'paid' && (
                          <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded font-semibold flex items-center gap-1" title={`${dpLabel} betaald${q.mollie_payment.paid_at ? ' op '+new Date(q.mollie_payment.paid_at).toLocaleString('nl-NL') : ''}`}>
                            € {dpLabel} betaald
                          </span>
                        )}
                        {q.mollie_payment?.status === 'open' && (
                          <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded font-semibold flex items-center gap-1" title={`${dpLabel} staat open`}>
                            € {dpLabel} open
                          </span>
                        )}
                        {(q.mollie_payment?.status === 'expired' || q.mollie_payment?.status === 'failed' || q.mollie_payment?.status === 'canceled') && (
                          <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded font-semibold flex items-center gap-1" title={`${dpLabel} ${q.mollie_payment.status}`}>
                            € {dpLabel} {q.mollie_payment.status}
                          </span>
                        )}
                        {!q.mollie_payment && (q.down_payment_percentage || 0) > 0 && (
                          <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-medium flex items-center gap-1" title={`${dpLabel}: ${q.down_payment_percentage}% van het totaal`}>
                            € {q.down_payment_percentage}%
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400">{q.offer_date}</p>
                      {q.status === 'goedgekeurd' && cr.signer_name && (
                        <p className="text-xs text-green-700">door {cr.signer_name}</p>
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">{q.client_name}</p>
                      <p className="text-xs text-gray-500">{q.description}</p>
                      {firstSchedule && (
                        <p className="text-xs text-[#3da5a5] mt-1 flex items-center gap-1" data-testid={`first-schedule-${q.id}`}>
                          📅 Eerste agenda: <strong>{firstSchedule.date}</strong>
                          {firstSchedule.time && <span className="text-gray-500">· {firstSchedule.time}</span>}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-lg font-bold text-[#3da5a5]">{fmtEuro(getQuoteTotals(q).total)}</p>
                      {q.status === 'converted' && <p className="text-xs text-green-600 font-medium">Factuur: {q.converted_to}</p>}
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      <Button variant="ghost" size="sm"
                        onClick={() => {
                          if (!q.public_token) { toast.error('Geen publieke link beschikbaar — open en bewaar de offerte eerst'); return; }
                          const url = `${window.location.origin}/offerte/${q.public_token}`;
                          navigator.clipboard.writeText(url).then(
                            () => toast.success('Link gekopieerd! Plak in WhatsApp/email.'),
                            () => toast.error('Kon niet kopiëren — open zelf: ' + url)
                          );
                        }}
                        className="text-purple-600 hover:bg-purple-50"
                        data-testid={`copy-link-${q.id}`}
                        title="Kopieer publieke link voor klant"
                      >
                        <Link2 className="w-4 h-4 mr-1" /><span className="text-xs">Link</span>
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setPreviewQuote(q)} className="text-blue-600" data-testid={`preview-quote-${q.id}`} title="Bekijken"><Eye className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleOpenEmail(q)} className="text-[#1a5276] hover:bg-blue-50" data-testid={`email-quote-${q.id}`} title="E-mail versturen"><Mail className="w-4 h-4 mr-1" /><span className="text-xs">Versturen</span></Button>
                      <Button variant="ghost" size="sm" onClick={() => setEmailHistoryQuote(q)} className="text-gray-500 hover:bg-gray-100" data-testid={`email-history-quote-${q.id}`} title="E-mailgeschiedenis"><Clock className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleConvertToInvoice(q.id, q.client_name)}
                        className={q.status === 'converted' ? "text-gray-400 hover:text-green-600 hover:bg-green-50" : "text-green-600 hover:text-green-700 hover:bg-green-50"}
                        data-testid={`convert-quote-${q.id}`} title={q.status === 'converted' ? 'Opnieuw factureren' : 'Omzetten naar factuur'}>
                        <ArrowRightLeft className="w-4 h-4 mr-1" /><span className="text-xs">{q.status === 'converted' ? 'Opnieuw' : 'Factureren'}</span>
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDuplicateNext(q)}
                        className="text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50"
                        data-testid={`duplicate-next-quote-${q.id}`}
                        title={`Volgende offerte (${_suggestNextLabel(q.down_payment_label)})`}>
                        <Copy className="w-4 h-4 mr-1" /><span className="text-xs">Volgende</span>
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDuplicate(q)}
                        className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                        data-testid={`duplicate-quote-${q.id}`}
                        title="Exacte kopie maken">
                        <Copy className="w-4 h-4 mr-1" /><span className="text-xs">Dupliceren</span>
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(q)} className="text-gray-600" title="Bewerken"><Edit className="w-4 h-4" /></Button>
                      <StatusActionDropdown
                        type="quote"
                        id={q.id}
                        currentStatus={q.status}
                        axiosInstance={axiosInstance}
                        onUpdated={fetchQuotes}
                      />
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(q.id)} className="text-red-500" title="Verwijderen"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                  <InstallmentInvoiceActions quote={q} axiosInstance={axiosInstance} onRefresh={fetchQuotes} />
                </div>
              </CardContent>
            </Card>
          );});
          })()}
        </div>
      )}

      <EmailSendModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        documentType="quote"
        document={previewQuote}
        pdfNode={printRef.current}
        publicLinkBase={window.location.origin}
        onSent={fetchQuotes}
      />

      <EmailHistoryModal
        open={!!emailHistoryQuote}
        onClose={() => setEmailHistoryQuote(null)}
        documentType="quote"
        documentId={emailHistoryQuote?.id}
        documentNumber={emailHistoryQuote?.number}
      />

      <RebrandPdfDialog open={rebrandOpen} onClose={() => setRebrandOpen(false)} />
      <ImportDocumentModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        documentType="quote"
        onSaved={() => { fetchQuotes(); }}
      />
    </div>
  );
};

export default Quotes;
